import type { LegacyRef } from 'react';
import type { PAGE_POSITIONS } from 'types';
import type { AffiliateStockListing, CapiProductPage } from 'types/capi/pdp';
import type { StockStatus as StockStatusType } from 'types/capi/shared';

import KlarnaPlacement from '~/components/KlarnaPlacement';
import AffiliateLogo from '~/components/Pdp/WhereToBuyInStock/AffiliateLogo';
import AffiliateSizes from '~/components/Pdp/WhereToBuyInStock/AffiliateSizes';
import ProductNotificationButton from '~/components/ProductNotificationButton';
import { getStockStatusConfig } from '~/domain/stockStatus';
import usePageData from '~/hooks/usePageData';
import { formatReleaseDateLong } from '~/utils/dates';
import {
  getCheapestListing,
  getPriceForSize,
  getStockStatusForSize
} from '~/utils/listings';

import DiscountCode from '../../DiscountCode';
import { BuyNow, VisitSite } from './Buttons';
import ShippingAndPrice from './ShippingAndPrice';

type Props = {
  listing: AffiliateStockListing;
  pagePosition: PAGE_POSITIONS;
  position: number;
  selectedSize?: string;
};

const StockListing = ({
  listing,
  position,
  selectedSize,
  pagePosition
}: Props) => {
  const product = usePageData<CapiProductPage>();

  const stockStatus = getStockStatusForSize(listing, selectedSize);
  const isMarketplace = listing.affiliate.id === 'affiliate:marketplace';

  const klarnaPlacementPrice = selectedSize
    ? (getPriceForSize(listing, selectedSize) ?? listing.price)
    : (getCheapestListing(listing) ?? listing.price);

  return (
    <div className="flex flex-col gap-4 rounded-lg bg-white p-4">
      <div className="flex gap-4 bg-white">
        <div className="flex grow items-center gap-4">
          <div className="h-9 w-16">
            <AffiliateLogo
              src={listing.affiliate.logo}
              alt={listing.affiliate.name}
            />
          </div>
          <div>
            <StockStatus
              stockStatus={stockStatus}
              listing={listing}
              selectedSize={selectedSize}
            />
          </div>
        </div>
        <ShippingAndPrice listing={listing} selectedSize={selectedSize} />
      </div>
      {isMarketplace && <KlarnaPlacement price={klarnaPlacementPrice} />}
      <AffiliateSizes
        product={product}
        price={listing.price}
        affiliate={listing.affiliate}
        selectedSize={selectedSize}
        sizes={listing.sizes}
        urlTemplates={listing}
        position={position}
        pagePosition={pagePosition}
      />
      <div className="flex w-full flex-col gap-2">
        <DiscountCode
          code={listing.discount_code?.code}
          amount={listing.discount_code?.amount}
        />
        <StockListingButtons
          stockStatus={stockStatus}
          selectedSize={selectedSize}
          listing={listing}
          position={position}
          pagePosition={pagePosition}
        />
      </div>
    </div>
  );
};

export const StockStatus = ({
  stockStatus,
  listing,
  selectedSize
}: {
  listing: AffiliateStockListing;
  selectedSize?: string;
  stockStatus: StockStatusType;
}) => {
  const { textColourClass } = getStockStatusConfig(stockStatus)!;

  switch (stockStatus) {
    case 'IN_STOCK':
      return (
        <>
          {listing.affiliate.is_reseller ? (
            <>
              <p className={`text-sm text-reseller-blue md:text-base`}>
                Marketplace
              </p>
              {listing.affiliate.id === 'affiliate:marketplace' && (
                <p className="text-xs text-black md:text-sm">Verified seller</p>
              )}
            </>
          ) : (
            <p className={`text-sm ${textColourClass} md:text-base`}>
              In stock
            </p>
          )}
        </>
      );
    case 'COMING_SOON':
      return (
        <div className="flex flex-col text-sm md:text-base">
          <p className={`${textColourClass}`}>Coming Soon</p>
          <span>
            {listing.release_date
              ? formatReleaseDateLong(listing.release_date)
              : 'TBC'}
          </span>
        </div>
      );
    case 'SOLD_OUT':
      return (
        <div className={`text-sm ${textColourClass} md:text-base`}>
          {listing.sizes.length && selectedSize ? (
            <>
              <p>Out of stock</p>
              <p className="text-black">UK {selectedSize}</p>
            </>
          ) : (
            'Sold out'
          )}
        </div>
      );
    case 'TBC':
    default:
      return <p className={textColourClass}>TBC</p>;
  }
};

type StockListingButtonsProps = {
  buttonsRef?: LegacyRef<HTMLDivElement>;
  dynamicVariant?: boolean;
  listing: AffiliateStockListing;
  onBuyNowClick?: () => void;
  pagePosition: PAGE_POSITIONS;
  position: number;
  selectedSize?: string;
  showAfffiliateLogo?: boolean;
  stockStatus: StockStatusType;
};

export const StockListingButtons: React.FC<StockListingButtonsProps> = ({
  stockStatus,
  pagePosition,
  selectedSize,
  listing,
  position,
  onBuyNowClick = () => {},
  showAfffiliateLogo,
  dynamicVariant
}) => {
  const product = usePageData<CapiProductPage>();

  return (
    <div data-cy="stock-listing-buttons">
      {stockStatus === 'IN_STOCK' ? (
        <div onClick={onBuyNowClick}>
          <BuyNow
            showAfffiliateLogo={showAfffiliateLogo}
            selectedSize={selectedSize}
            stockListing={listing}
            buttonPosition={position}
            pagePosition={pagePosition}
            variant={
              !dynamicVariant
                ? 'forest'
                : selectedSize
                  ? 'green-cta'
                  : 'black-cta'
            }
          />
        </div>
      ) : (
        <div
          className={
            product.allow_user_notifications ? 'grid grid-cols-2 gap-4' : ''
          }
        >
          <VisitSite
            stockListing={listing}
            buttonPosition={position}
            pagePosition={pagePosition}
          />
          {product.allow_user_notifications && (
            <ProductNotificationButton
              product={{
                affiliateName: listing.affiliate.name,
                id: product.product_id,
                image: product.gallery[0],
                legacyId: product.legacy_id,
                name: product.name,
                price: product.price.regular
              }}
              className="h-12"
              pagePosition="WTB"
              buttonStyle="normal"
            />
          )}
        </div>
      )}
    </div>
  );
};

export default StockListing;
